/**
 * Primary navigation component.
 *
 * Renders the bottom tab bar as evenly spaced tabs: Home and Activity, plus
 * Swap and Earn when their testnet feature gates allow them. There is no settings gear, divider,
 * or status dot in the
 * bar; Settings is reached from the gear in the home header, and the connection
 * and tier state surface there and on the balances rather than crowding the tab
 * bar. It inherits the design tokens and the app-nav component styles declared in
 * the cascade layers, so nothing needs to be re-imported here.
 *
 * Approvals are not a top-level tab: the per-wallet Approvals tab, the gear-linked
 * Settings entry, and a policy-held Send all lead into the approvals surface, so a
 * sixth bottom tab would duplicate a place the user already has.
 *
 * Navigation goes through react-router. Each tab is a NavLink, so react-router
 * resolves the active route and toggles is-active on the matching link. The
 * active tab is read from the live route, so it stays correct however the view
 * was reached.
 */

import { NavLink } from 'react-router-dom';
import { useT } from '@/i18n';
import type { MessageKey } from '@/i18n/en';
import { useSlice } from '@/store/hooks';
import { lendingEnabled, swapEnabled, type NetworkInfo } from '@/lib/network';
import { ROUTE_SWAP } from '@/lib/routes';
import { requestHomeRefresh } from '@/lib/homeRefresh';

/**
 * The tabs in display order. Each entry is a route the element links to. Home
 * points at the root route; its label reads "Home" rather than "Dashboard" so
 * the short word never has to truncate in a narrow tab.
 *
 * Swap follows Activity, but only in testnet mode: swaps are testnet-only for
 * now, so the tab is inserted when swapEnabled is true and is
 * absent on mainnet, which is the default tier. The /swap route and the swap
 * glyph stay in place regardless, so widening the gate later is a one-line
 * change in swapEnabled.
 */
interface Tab {
  path: string;
  labelKey: MessageKey;
  icon: IconName;
}

const BASE_TABS: Tab[] = [
  { path: '/', labelKey: 'nav.home', icon: 'home' },
  { path: '/activity', labelKey: 'activity.title', icon: 'activity' },
];

/** The Swap tab, inserted after Activity only in testnet mode (see swapEnabled). */
const SWAP_TAB: Tab = { path: ROUTE_SWAP, labelKey: 'swap.title', icon: 'swap' };

/** The Earn tab, inserted after Swap only in testnet mode (see lendingEnabled). */
const EARN_TAB: Tab = { path: '/earn', labelKey: 'earn.title', icon: 'earn' };

type IconName = 'home' | 'activity' | 'swap' | 'earn';

/**
 * Inline SVG glyphs for each tab. Kept as small path strings so the nav needs no
 * icon font or image fetch on the critical path.
 */
const ICONS: Record<IconName, { viewBox: string; paths: string[] }> = {
  home: {
    viewBox: '0 0 24 24',
    paths: ['M3 11.5 12 4l9 7.5', 'M5 10v10h14V10', 'M10 20v-6h4v6'],
  },
  activity: {
    viewBox: '0 0 24 24',
    paths: ['M3 12h4l2.5 7L14 5l2.5 7H21'],
  },
  swap: {
    viewBox: '0 0 24 24',
    paths: ['M4 7h13', 'M14 3l3 4-3 4', 'M20 17H7', 'M10 13l-3 4 3 4'],
  },
  earn: {
    viewBox: '0 0 24 24',
    // A percent glyph: two small circles and the diagonal stroke.
    paths: [
      'M19 5 5 19',
      'M7.5 9a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z',
      'M16.5 18a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z',
    ],
  },
};

/**
 * An inline SVG icon from the glyph table. Marked decorative since the adjacent
 * text label carries the accessible name.
 */
function Icon({ name }: { name: IconName }) {
  const glyph = ICONS[name] ?? ICONS.home;
  return (
    <svg
      viewBox={glyph.viewBox}
      width="24"
      height="24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className="app-nav__icon"
    >
      {glyph.paths.map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}

/**
 * The primary navigation. Rendered once a session is present, so the sign-in
 * screen never shows it.
 */
export default function AppNav() {
  const t = useT();
  const net = useSlice('network');
  const wallets = useSlice('wallets');
  const info: NetworkInfo = {
    servesMainnet: net.servesMainnet,
    servesTestnet: net.servesTestnet,
    selectedEnv: net.selectedEnv,
    bitcoinNetwork: net.bitcoinNetwork,
    environment: net.environment,
  };
  // The lending market lives on Sui, so the Earn tab only makes sense while a
  // Sui wallet is the active account: any other selection (another chain, or
  // the All Wallets aggregate) hides the tab rather than offering a screen
  // that cannot act on the selected wallet.
  const selectedWallet = wallets.list.find((w) => w.id === wallets.selectedId);
  const suiSelected = (selectedWallet?.chain ?? '').toLowerCase().startsWith('sui');
  // Reveal the Swap and Earn tabs only in testnet mode, after Activity.
  const testnetTabs = [
    ...(swapEnabled(info) ? [SWAP_TAB] : []),
    ...(lendingEnabled(info) && suiSelected ? [EARN_TAB] : []),
  ];
  const tabs = [...BASE_TABS, ...testnetTabs];
  return (
    <nav className="app-nav" role="navigation" aria-label={t('nav.primary')}>
      <div className="app-nav__tabs">
        {tabs.map((tab) => (
          <NavLink
            key={tab.path}
            to={tab.path}
            end={tab.path === '/'}
            onClick={() => {
              if (tab.path === '/') requestHomeRefresh();
            }}
            className={({ isActive }) => (isActive ? 'app-nav__link is-active' : 'app-nav__link')}
          >
            <Icon name={tab.icon} />
            <span className="app-nav__label">{t(tab.labelKey)}</span>
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
